import type { ReactElement } from 'react';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';

import { routePaths } from '../app/route-paths.js';
import styles from '../components/layout/AuthLayout.module.css';
import { Button } from '../components/ui/Button.js';
import { FormError } from '../components/ui/FormError.js';
import { authClient } from '../lib/auth-client.js';
import { authErrorMessage } from '../lib/auth-errors.js';

/** Shown after sign-up (and after a sign-in attempt on an unverified account). */
export function CheckEmailPage(): ReactElement {
    const [params] = useSearchParams();
    const email = params.get('email');
    const [status, setStatus] = useState<'idle' | 'sending' | 'sent'>('idle');
    const [serverError, setServerError] = useState<string | null>(null);

    async function resend(): Promise<void> {
        if (email === null) {
            return;
        }
        setServerError(null);
        setStatus('sending');
        const result = await authClient.sendVerificationEmail({
            email,
            callbackURL: routePaths.verifyEmail,
        });
        if (result.error) {
            setServerError(authErrorMessage(result.error, 'Could not send the email'));
            setStatus('idle');
            return;
        }
        setStatus('sent');
    }

    return (
        <>
            <h1>Check your email</h1>
            <p>
                We sent a verification link{email === null ? '' : ' to '}
                {email === null ? null : <strong>{email}</strong>}. Open it on this device to finish
                setting up your account.
            </p>
            <FormError message={serverError} />
            {status === 'sent' ? (
                <p role="status">Sent again. Give it a minute to arrive.</p>
            ) : (
                email !== null && (
                    <Button
                        type="button"
                        variant="secondary"
                        loading={status === 'sending'}
                        onClick={() => {
                            void resend();
                        }}
                    >
                        Send the email again
                    </Button>
                )
            )}
            <p className={styles.links}>
                <Link to={routePaths.signIn}>Back to sign in</Link>
            </p>
        </>
    );
}
